import { useEffect, useRef, useState, useCallback } from 'react'
import type { RepoMap } from '../../types'
import { tokenUtils } from './token-utils'

type ConnectionStatus =
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'error'
  | 'unauthorized'

interface UseWebSocketResult {
  connectionStatus: ConnectionStatus
  repoWorkflows: RepoMap
  connect: (token: string) => void
  disconnect: () => void
}

const RECONNECT_DELAYS = [1000, 2000, 5000, 10000, 30000] // Progressive backoff
const MAX_RECONNECT_ATTEMPTS = 5

export function useWebSocket(): UseWebSocketResult {
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>('disconnected')
  const [repoWorkflows, setRepoWorkflows] = useState<RepoMap>({})

  const socketRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<number | null>(null)
  const reconnectAttemptRef = useRef(0)
  const shouldReconnectRef = useRef(false)
  const currentTokenRef = useRef<string | null>(null)

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
  }, [])

  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false
    clearReconnectTimeout()

    if (socketRef.current) {
      socketRef.current.close()
      socketRef.current = null
    }

    setConnectionStatus('disconnected')
    reconnectAttemptRef.current = 0
  }, [clearReconnectTimeout])

  const createConnection = useCallback(async (token: string) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.close()
    }

    // First, validate the token by making a test request
    try {
      const response = await fetch('/api/test-auth', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.status === 401 || response.status === 403) {
        console.log(
          'Token validation failed with auth error, requiring re-login',
        )
        setConnectionStatus('unauthorized')
        shouldReconnectRef.current = false
        tokenUtils.removeToken()
        currentTokenRef.current = null
        return
      }

      // For other HTTP errors (5xx, network issues), continue with WebSocket attempt
      if (!response.ok) {
        console.log(
          `Token validation returned ${response.status}, continuing with WebSocket attempt`,
        )
      }
    } catch (error) {
      // If we can't reach the server, continue with WebSocket attempt
      console.log(
        'Unable to validate token via HTTP, attempting WebSocket connection',
        error,
      )
    }

    const socketProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const socket = new WebSocket(
      `${socketProtocol}://${window.location.host}?token=${token}`,
    )

    socketRef.current = socket
    setConnectionStatus('connecting')

    socket.onopen = () => {
      console.log('WebSocket connected')
      setConnectionStatus('connected')
      reconnectAttemptRef.current = 0 // Reset reconnect attempts on successful connection
      socket.send('Gimme jobs')
    }

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        setRepoWorkflows(data)
      } catch (error) {
        console.error('Error parsing WebSocket message:', error)
      }
    }

    socket.onclose = (event) => {
      console.log('WebSocket closed:', event.code, event.reason)
      socketRef.current = null

      // Handle different close codes
      if (event.code === 1000) {
        // Normal closure - don't reconnect
        setConnectionStatus('disconnected')
        shouldReconnectRef.current = false
        return
      }

      // Before treating as unauthorized, check if token is actually expired
      if (
        currentTokenRef.current &&
        tokenUtils.isTokenExpired(currentTokenRef.current)
      ) {
        console.log('Token has expired, requiring re-authentication')
        setConnectionStatus('unauthorized')
        shouldReconnectRef.current = false
        tokenUtils.removeToken()
        currentTokenRef.current = null
        return
      }

      // For any other close codes (including 1006), try to reconnect with existing token
      // This handles tab inactivity, network issues, server restarts, etc.
      setConnectionStatus('disconnected')

      if (
        shouldReconnectRef.current &&
        reconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS &&
        currentTokenRef.current
      ) {
        const delay =
          RECONNECT_DELAYS[
            Math.min(reconnectAttemptRef.current, RECONNECT_DELAYS.length - 1)
          ]
        console.log(
          `Attempting to reconnect in ${delay}ms (attempt ${reconnectAttemptRef.current + 1}/${MAX_RECONNECT_ATTEMPTS})`,
        )

        reconnectTimeoutRef.current = window.setTimeout(() => {
          if (shouldReconnectRef.current && currentTokenRef.current) {
            // Double-check token expiration before reconnecting
            if (tokenUtils.isTokenExpired(currentTokenRef.current)) {
              setConnectionStatus('unauthorized')
              shouldReconnectRef.current = false
              tokenUtils.removeToken()
              currentTokenRef.current = null
              return
            }

            reconnectAttemptRef.current++
            createConnection(currentTokenRef.current)
          }
        }, delay)
      } else if (reconnectAttemptRef.current >= MAX_RECONNECT_ATTEMPTS) {
        console.log('Max reconnection attempts reached')
        setConnectionStatus('error')
        shouldReconnectRef.current = false
      }
    }

    socket.onerror = (error) => {
      console.error('WebSocket error:', error)
      // Don't immediately set to error state, let onclose handle it
    }
  }, [])

  const connect = useCallback(
    (token: string) => {
      currentTokenRef.current = token
      shouldReconnectRef.current = true
      reconnectAttemptRef.current = 0
      clearReconnectTimeout()

      // Save token for persistence
      tokenUtils.saveToken(token)

      createConnection(token)
    },
    [createConnection, clearReconnectTimeout],
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      shouldReconnectRef.current = false
      clearReconnectTimeout()
      if (socketRef.current) {
        socketRef.current.close()
      }
    }
  }, [clearReconnectTimeout])

  return {
    connectionStatus,
    repoWorkflows,
    connect,
    disconnect,
  }
}
